import { Button, ButtonGroup } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { CheckCircle2, XCircle } from 'lucide-react';

import type { MarkingStatus } from './types';

export function AssessmentButtonGroup({
  status,
  showBoth = false,
  disabled = false,
  isLoading = false,
  loadingType,
  onRevisionClick,
  onCorrectClick,
  visuallyEnabled = false,
}: {
  status: MarkingStatus;
  showBoth?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  loadingType?: MarkingStatus;
  onRevisionClick?: () => void;
  onCorrectClick?: () => void;
  /**
   * When true, buttons appear visually enabled (not grayed out) but are non-interactive.
   * Used for auto-graded questions where the marking status should be visible but not editable.
   * The buttons will have no click handlers and show a "not-allowed" cursor.
   */
  visuallyEnabled?: boolean;
}) {
  return (
    <ButtonGroup size="sm" spacing={1}>
      {showBoth || status === 'REVISION' ? (
        <Button
          aria-label="Revision"
          type="button"
          borderRadius="md"
          borderColor="red.500"
          colorScheme="red"
          variant={status === 'REVISION' ? 'solid' : 'ghost'}
          onClick={visuallyEnabled ? undefined : onRevisionClick}
          leftIcon={<XCircle size={16} />}
          isDisabled={visuallyEnabled ? false : disabled}
          isLoading={isLoading && loadingType === 'REVISION' ? true : false}
          cursor={visuallyEnabled ? 'not-allowed' : undefined}
        >
          <Trans>Revision</Trans>
        </Button>
      ) : null}
      {showBoth || status === 'CORRECT' ? (
        <Button
          aria-label="Correct"
          type="button"
          borderRadius="md"
          borderColor="green.500"
          colorScheme="green"
          variant={status === 'CORRECT' ? 'solid' : 'ghost'}
          onClick={visuallyEnabled ? undefined : onCorrectClick}
          leftIcon={<CheckCircle2 size={16} />}
          isDisabled={visuallyEnabled ? false : disabled}
          isLoading={isLoading && loadingType === 'CORRECT' ? true : false}
          cursor={visuallyEnabled ? 'not-allowed' : undefined}
        >
          <Trans>Correct</Trans>
        </Button>
      ) : null}
    </ButtonGroup>
  );
}
